/**
 * @file App.tsx
 * @description Root application router. Handles role-based routing and
 * renders per-role inactivity timeout guards.
 *
 * Role → Route mapping:
 *  SUPER_ADMIN → /superadmin (SuperAdminShell — 30 nav items: 6 SA-exclusive + 24 admin shared)
 *  ADMIN       → /admin      (AdminShell — 27 nav items)
 *  EMPLOYEE    → /employee (NON_TEACHING) or /teacher (TEACHING — auto-redirected by EmployeeShell)
 *  STUDENT     → /student
 *  APP_OWNER   → /owner      (OwnerShell — 22 cards + 19 nav items)
 *
 * Inactivity timeouts:
 *  Admin / SUPER_ADMIN → 30 min
 *  Employee / Student  → 60 min
 *  App Owner           → 15 min
 *
 * ⚠️ DEV_BYPASS_AUTH: Set to true to skip login and test portals directly.
 *    REMOVE before production. Change DEV_BYPASS_ROLE to test different portals.
 *
 * Save to: client/src/App.tsx
 */
import { Routes, Route, Navigate } from 'react-router-dom';
import { useContext, useEffect, useState, lazy, Suspense } from 'react';
import { AuthContext } from './context/AuthContext';
import ProtectedRoute from './components/ui/ProtectedRoute';
import { useInactivityTimer } from './hooks/useInactivityTimer';
import { usePushDeepLinkRead } from './hooks/usePushDeepLinkRead';
import TimeoutModal from './components/ui/TimeoutModal';
import SkeletonLoader from './components/ui/SkeletonLoader';
import Splash from './components/ui/Splash';
import PWAInstallPrompt from './components/ui/PWAInstallPrompt';
// [REQ-AUG-30 ACCESS-1 1.0a, ruling Q13] Route-level capability gate — see the component header.
import CapabilityGate from './components/access/CapabilityGate';
// [QPGEN-1 Q3b] the AI Question Papers door: the school's `qpgen` flag and a qpgen role, else the portal's home
import { QpgenRoute } from './pages/qpgen/QpgenGate';

/* ══════════════════════════════════════════════════════════════
   ⚠️  DEV BYPASS — REMOVE BEFORE PRODUCTION
   Change DEV_BYPASS_ROLE to test different portals:
     'EMPLOYEE'    → /employee   (non-teaching dashboard)
     'TEACHER'     → /teacher    (teaching dashboard — still EMPLOYEE role)
     'STUDENT'     → /student
     'ADMIN'       → /admin      (AdminShell — 27 nav items)
     'SUPER_ADMIN' → /superadmin (SuperAdminShell — 30 nav items)
     'APP_OWNER'   → /owner      (OwnerShell — 19 nav items, 22 cards)
   Set DEV_BYPASS_AUTH = false to restore normal login flow.
   ══════════════════════════════════════════════════════════════ */
const DEV_BYPASS_AUTH = false;
const DEV_BYPASS_ROLE: string = 'APP_OWNER'; // ← change this to test different portals

const DEV_MOCK_USERS: Record<string, object> = {
  TEACHER: {
    id: 'dev-teacher-001',
    name: 'Priya Nair',
    email: 'priya@demo.school',
    role: 'EMPLOYEE',
    employeeCategory: 'TEACHING',
    schoolId: 'school-001',
    schoolName: 'Delhi Public School',
    designation: 'PGT Mathematics',
  },
  EMPLOYEE: {
    id: 'dev-employee-001',
    name: 'Ramesh Kumar',
    email: 'ramesh@demo.school',
    role: 'EMPLOYEE',
    employeeCategory: 'NON_TEACHING',
    schoolId: 'school-001',
    schoolName: 'Delhi Public School',
    designation: 'Peon',
  },
  STUDENT: {
    id: 'dev-student-001',
    name: 'Aryan Kapoor',
    email: 'aryan@demo.school',
    role: 'STUDENT',
    schoolId: 'school-001',
    schoolName: 'Delhi Public School',
  },
  ADMIN: {
    id: 'dev-admin-001',
    name: 'Admin User',
    email: 'admin@demo.school',
    role: 'ADMIN',
    schoolId: 'school-001',
    schoolName: 'Delhi Public School',
  },
  SUPER_ADMIN: {
    id: 'dev-sa-001',
    name: 'Super Admin',
    email: 'sa@demo.school',
    role: 'SUPER_ADMIN',
    schoolId: 'school-001',
    schoolName: 'Delhi Public School',
  },
  APP_OWNER: {
    id: 'dev-owner-001',
    name: 'App Owner',
    email: 'owner@aisistoo.com',
    role: 'APP_OWNER',
  },
};
/* ══════════════════════════════════════════════════════════════ */

/* ── Auth pages (always bundled — small, critical path) ── */
import LoginSchoolPage from './pages/auth/LoginSchoolPage';
import LoginOwnerPage from './pages/auth/LoginOwnerPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import FirstLoginChangePage from './pages/auth/FirstLoginChangePage';
import { useBusyClaim } from './pwa/useBusyClaim';
/* ── Lazy-loaded portal shells ── */
const AdminShell = lazy(() => import('./pages/admin/AdminShell'));
const SuperAdminShell = lazy(() => import('./pages/superadmin/SuperAdminShell'));
const EmployeeShell = lazy(() => import('./pages/employee/EmployeeShell'));
const TeacherShell = lazy(() => import('./pages/teacher/TeacherShell'));
const StudentShell = lazy(() => import('./pages/student/StudentShell'));
const OwnerShell = lazy(() => import('./pages/owner/OwnerShell'));
const SchoolLinkPage = lazy(() => import('./pages/auth/SchoolLinkPage'));
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage'));
const OlympiadSignupPage = lazy(() => import('./pages/auth/OlympiadSignupPage'));
const VerifyEmailPage = lazy(() => import('./pages/auth/VerifyEmailPage'));
const VerifyTcPage = lazy(() => import('./pages/auth/VerifyTcPage')); // VERIFY-TC-1 (public TC QR target)
const ShieldSurveyPage = lazy(() => import('./pages/shield/ShieldSurveyPage')); // SHIELD-1 / S3 (public)
const ShieldVerifyPage = lazy(() => import('./pages/shield/ShieldVerifyPage')); // SHIELD-1 / S6 (public, the card's QR target)
const PrivacyPolicyPage = lazy(() => import('./pages/legal/PrivacyPolicyPage')); // COMPLY-1 C3
const TermsOfUsePage = lazy(() => import('./pages/legal/TermsOfUsePage'));
const StudentDataSafeguardsPage = lazy(() => import('./pages/legal/StudentDataSafeguardsPage'));
const LanguageSelectorPage = lazy(() => import('./pages/auth/LanguageSelectorPage'));
const OlympiadExamPage = lazy(() => import('./pages/student/olympiad/OlympiadExamPage')); // OLYMPIAD-P4-A (full-screen, outside StudentShell)
const SchoolProfilePage = lazy(() => import('./pages/SchoolProfilePage')); // SCHOOL-PROFILE-VIS: read-only, shared by student/teacher/employee

/* ── Lazy-loaded teacher screens ── */
const TeacherDashboard = lazy(() => import('./pages/teacher/TeacherDashboard'));
const TeacherSchedule = lazy(() => import('./pages/teacher/screens/TeacherSchedule'));
const TeacherClasses = lazy(() => import('./pages/teacher/screens/TeacherClasses'));
const TeacherDiary = lazy(() => import('./pages/teacher/screens/TeacherDiary'));
const TeacherPTM = lazy(() => import('./pages/teacher/screens/TeacherPTM'));
const TeacherAttendance = lazy(() => import('./pages/teacher/screens/TeacherAttendance'));
const TeacherMarksEntry = lazy(() => import('./pages/teacher/screens/TeacherMarksEntry'));
const TeacherLeave = lazy(() => import('./pages/teacher/screens/TeacherLeave'));
// [EYEBALL-FIX-4 H4] TeacherShell has no command palette, so the nav item + this route are the TWO — and
// only two — registration points. Asserting "four" here (the Admin/SA number) would have been wrong.
const TeacherApprovals = lazy(() => import('./pages/teacher/screens/TeacherApprovals'));
const TeacherSalary = lazy(() => import('./pages/teacher/screens/TeacherSalary'));
const TeacherProfile = lazy(() => import('./pages/teacher/screens/TeacherProfile'));
const TeacherMyShift = lazy(() => import('./pages/teacher/screens/TeacherMyShift'));
const StaffDirectory = lazy(() => import('./pages/teacher/screens/StaffDirectory'));
const TeacherNotifications = lazy(() => import('./pages/teacher/screens/TeacherNotifications'));
const NotificationPreferences = lazy(() => import('./pages/settings/NotificationPreferences'));
const TeacherHolidayCalendar = lazy(() => import('./pages/teacher/screens/TeacherHolidayCalendar'));
const TeacherStudentProfile = lazy(() => import('./pages/teacher/screens/TeacherStudentProfile'));
const TeacherConnections = lazy(() => import('./pages/teacher/screens/TeacherConnections')); // CONNECT-2 follow-up: teacher connections screen
// [QPGEN-1 Q3b] the AI Question Papers screens, one chunk, run only behind QpgenRoute (the `qpgen` flag + a teacher;
// the service worker's precache may still hold the file)
const QpgenRoutes = lazy(() => import('./pages/qpgen/QpgenRoutes'));
/*
 * [REQ-AUG-30 ACCESS-1 1.0a] The library desk, MOUNTED for staff rather than copied.
 *
 * Same component the Admin and SuperAdmin shells render. It takes no props and reads the caller's
 * identity from the token, so there is nothing to fork — the precedent is `EmployeeAttendance` at
 * `/teacher/my-attendance` below, whose comment reads "it takes no props and self-gates, so it is
 * mounted, not copied". A second copy of a library screen is the thing that drifts.
 */
const AdminLibrary = lazy(() => import('./pages/admin/AdminLibrary'));
/*
 * [REQ-AUG-30 ACCESS-1 1.0b] The collection desk, MOUNTED for staff rather than copied — the same
 * component the Admin and SuperAdmin shells render, and the same reasoning as `AdminLibrary` above.
 * It takes no props and reads the caller's identity from the token, so there is nothing to fork.
 */
const AdminFeeCollection = lazy(() => import('./pages/admin/AdminFeeCollection'));

/* ── Lazy-loaded employee screens ── */
const EmployeeDashboard = lazy(() => import('./pages/employee/EmployeeDashboard'));
const EmployeeAttendance = lazy(() => import('./pages/employee/screens/EmployeeAttendance'));
// [REQ-AUG-26 Band 3] the driver/conductor ops surface. Mounted in BOTH staff portals because a
// driver is an ordinary NON_TEACHING employee and a conductor may be either — the screen self-gates
// on the transport flag and shows only trips the signed-in crew member is assigned to.
const TransportTripRoster = lazy(() => import('./pages/employee/TransportTripRoster'));
const MyTickets = lazy(() => import('./pages/shared/MyTickets')); // [PORTAL-FIX-1 R3] shared across teacher + employee
const EmployeeLeave = lazy(() => import('./pages/employee/screens/EmployeeLeave'));
const EmployeeSalary = lazy(() => import('./pages/employee/screens/EmployeeSalary'));
const EmployeeProfile = lazy(() => import('./pages/employee/screens/EmployeeProfile'));
const EmployeeMyShift = lazy(() => import('./pages/employee/screens/EmployeeMyShift'));
const EmployeeNotifications = lazy(() => import('./pages/employee/screens/EmployeeNotifications'));
const EmployeeHolidayCalendar = lazy(() => import('./pages/employee/screens/EmployeeHolidayCalendar'));

/** Maps a user role to the inactivity timeout in milliseconds. */
function getTimeoutMs(role: string | undefined): number {
  if (!role) return 30 * 60 * 1000;
  if (role === 'SUPER_ADMIN' || role === 'ADMIN') return 30 * 60 * 1000;
  if (role === 'APP_OWNER') return 15 * 60 * 1000;
  return 60 * 60 * 1000; // EMPLOYEE, STUDENT
}

/**
 * Maps a user role to its default redirect path.
 *
 * SUPER_ADMIN now routes to /superadmin (its own shell) instead of
 * sharing /admin. SuperAdminShell mounts at /superadmin/* and includes
 * 6 SA-exclusive screens + 24 reused admin screens.
 */
function getDefaultPath(role: string | undefined, employeeCategory?: string): string {
  switch (role) {
    case 'SUPER_ADMIN':
      return '/superadmin';
    case 'ADMIN':
      return '/admin';
    case 'EMPLOYEE':
      return employeeCategory === 'TEACHING' ? '/teacher' : '/employee';
    case 'STUDENT':
      return '/student';
    case 'APP_OWNER':
      return '/owner';
    default:
      return '/login';
  }
}

/** Full-page suspense fallback using skeleton cards. */
function PageLoader() {
  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <SkeletonLoader variant="card" />
      <SkeletonLoader variant="card" />
      <SkeletonLoader variant="card" />
    </div>
  );
}

/**
 * InactivityGuardInner — the actual hook-using guard (only rendered in production).
 */
function InactivityGuardInner({
  role,
  children,
}: {
  role: string | undefined;
  children: React.ReactNode;
}) {
  const timeoutMs = getTimeoutMs(role);
  const { showWarning, extend, logout } = useInactivityTimer({
    timeoutMs,
    warningMs: 2 * 60 * 1000,
  });

  // PWA-UPDATE-1: a reload while the "you are about to be signed out" countdown is up would silently
  // dismiss it and restart the clock. On a shared school desk that is the difference between a
  // session ending and a session staying open on someone else's account.
  useBusyClaim(showWarning, 'inactivity-warning');

  return (
    <>
      {children}
      {showWarning && (
        <TimeoutModal
          role={role ?? 'ADMIN'}
          onContinue={extend}
          onLogout={logout}
        />
      )}
    </>
  );
}

/**
 * InactivityGuard wraps authenticated sections with an inactivity timer.
 * Shows a 2-minute countdown warning modal before auto-logout.
 * Skipped entirely when DEV_BYPASS_AUTH is true (no real auth session).
 */
function InactivityGuard({
  role,
  children,
}: {
  role: string | undefined;
  children: React.ReactNode;
}) {
  if (DEV_BYPASS_AUTH) {
    return <>{children}</>;
  }
  return <InactivityGuardInner role={role}>{children}</InactivityGuardInner>;
}

/**
 * DevOrProtected — renders children without ProtectedRoute when DEV_BYPASS_AUTH is on.
 * In production (DEV_BYPASS_AUTH = false), delegates to ProtectedRoute as normal.
 */
function DevOrProtected({
  roles,
  children,
}: {
  roles: string[];
  children: React.ReactNode;
}) {
  if (DEV_BYPASS_AUTH) {
    return <>{children}</>;
  }
  return <ProtectedRoute roles={roles}>{children}</ProtectedRoute>;
}

/**
 * App — top-level router.
 *
 * EMPLOYEE role routes to /employee. EmployeeShell detects if user
 * is TEACHING category and redirects to /teacher. Both routes are
 * protected with the EMPLOYEE role since there is NO 'TEACHER' role
 * in the UserRole enum.
 *
 * SUPER_ADMIN gets its own dedicated /superadmin/* route loading
 * SuperAdminShell. ADMIN stays on /admin/* loading AdminShell.
 */
export default function App() {
  const auth = useContext(AuthContext);

  useEffect(() => {
    const handler = (e: WheelEvent) => {
      const el = document.activeElement as HTMLElement | null;
      if (el && el instanceof HTMLInputElement && el.type === 'number' && el === e.target) {
        el.blur();
      }
    };
    document.addEventListener('wheel', handler, { passive: true });
    return () => document.removeEventListener('wheel', handler);
  }, []);

  /*
   * DEV BYPASS: Override user from context with mock user.
   * The mock user is injected into the context via the provider,
   * but since we can't modify the provider here, we use the mock
   * for routing decisions only. Components using useAuth() will
   * still get null — but DEMO_MODE in each card handles that.
   */
  const realUser = auth?.user;
  const user = DEV_BYPASS_AUTH
    ? (DEV_MOCK_USERS[DEV_BYPASS_ROLE] as Record<string, string> | undefined) ?? null
    : realUser;

  const userRole = (user as Record<string, string> | null)?.role;
  const employeeCategory = (user as Record<string, string> | null)?.employeeCategory;

  usePushDeepLinkRead();

  // BRAND-3 (item 4): hold the splash for at least its full animation length
  // (~2.6s) so it never dismisses mid-play — the effective display is
  // max(authReady, minDelay). Reduced-motion skips the hold (instant dismiss).
  const [minSplashDone, setMinSplashDone] = useState(false);
  useEffect(() => {
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setMinSplashDone(true);
      return;
    }
    const t = setTimeout(() => setMinSplashDone(true), 2600);
    return () => clearTimeout(t);
  }, []);

  // SPL-1: cover the auth-resolve window (validateSession) so a logged-in user
  // hard-refreshing never flashes the login screen. The splash also plays its
  // full animation (min-duration above) before dismissing.
  if (auth?.isLoading || !minSplashDone) return <Splash />;

  return (
    <>
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* ── Public: Auth screens ── */}
        <Route path="/login" element={<LoginSchoolPage />} />
        <Route path="/owner/login" element={<LoginOwnerPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/change-password" element={<FirstLoginChangePage />} />        <Route path="/s/:slug" element={<SchoolLinkPage />} />
        <Route path="/s/:slug/register" element={<RegisterPage />} />
        <Route path="/olympiad/signup" element={<OlympiadSignupPage />} />
        <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
        <Route path="/verify-tc/:token" element={<VerifyTcPage />} />{/* VERIFY-TC-1 — printed TC QR target */}
        {/*
          [SHIELD-1 / S3+S6] The public parent survey and its receipt. FLAT and OUTSIDE
          ProtectedRoute — a parent has no login, and neither does whoever a card is forwarded to.

          🔴 `/shield/verify/:verificationId` IS DECLARED FIRST AND THE ORDER IS LOAD-BEARING. React
          Router matches in order, so with `/shield/:slug` first a receipt lookup would be read as a
          school slug named "verify" and every scanned card would land on the survey page's
          not-found. Same shadowing that made `/verify-olympiad-cert` a missing route, and the
          reason ruling OQ-12 forbids RSM's slug being `verify`. The server's own router carries the
          identical ordering for the identical reason.
        */}
        <Route path="/shield/verify/:verificationId" element={<ShieldVerifyPage />} />{/* SHIELD-1 / S6 */}
        <Route path="/shield/:slug" element={<ShieldSurveyPage />} />{/* SHIELD-1 / S3 */}
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfUsePage />} />
        <Route path="/student-data-safeguards" element={<StudentDataSafeguardsPage />} />{/* COMPLY-1 C3 */}
        <Route path="/select-language" element={<LanguageSelectorPage />} />

        {/* ── Admin (ADMIN role only) ── */}
        <Route
          path="/admin/*"
          element={
            <DevOrProtected roles={['ADMIN']}>
              <InactivityGuard role={userRole}>
                <AdminShell />
              </InactivityGuard>
            </DevOrProtected>
          }
        />

        {/* ── Super Admin (SUPER_ADMIN role only) ── */}
        <Route
          path="/superadmin/*"
          element={
            <DevOrProtected roles={['SUPER_ADMIN']}>
              <InactivityGuard role={userRole}>
                <SuperAdminShell />
              </InactivityGuard>
            </DevOrProtected>
          }
        />

        {/* ── Teacher (TEACHING category employees) ── */}
        <Route
          path="/teacher"
          element={
            <DevOrProtected roles={['EMPLOYEE']}>
              <InactivityGuard role={userRole}>
                <TeacherShell />
              </InactivityGuard>
            </DevOrProtected>
          }
        >
          <Route index element={<TeacherDashboard />} />
          <Route path="schedule" element={<TeacherSchedule />} />
          <Route path="classes" element={<TeacherClasses />} />
          <Route path="diary" element={<TeacherDiary />} />
          <Route path="ptm" element={<TeacherPTM />} />
          <Route path="attendance" element={<TeacherAttendance />} />
          {/* [PORTAL-FIX-1 R1] The teacher's OWN check-in. Same component as /employee/attendance —
              it takes no props and self-gates on staffAttendance, so it is mounted, not copied. */}
          <Route path="my-attendance" element={<EmployeeAttendance />} />
          <Route path="my-trips" element={<TransportTripRoster />} />{/* REQ-AUG-26 Band 3 */}
          <Route path="marks" element={<TeacherMarksEntry />} />
          {/* [EYEBALL-FIX-4 H4] Registration point 2 of 2 for the teacher approvals screen. */}
          <Route path="approvals" element={<TeacherApprovals />} />
          <Route path="leave" element={<TeacherLeave />} />
          <Route path="salary" element={<TeacherSalary />} />
          <Route path="profile" element={<TeacherProfile />} />
          <Route path="shift" element={<TeacherMyShift />} />
          <Route path="staff" element={<StaffDirectory />} />
          <Route path="notifications" element={<TeacherNotifications />} />
          <Route path="my-tickets" element={<MyTickets />} />{/* [PORTAL-FIX-1 R3] */}
          <Route path="notification-preferences" element={<NotificationPreferences />} />
          <Route path="holidays" element={<TeacherHolidayCalendar />} />
          <Route path="connections" element={<TeacherConnections />} />{/* CONNECT-2 follow-up (screen self-gates on studentConnections) */}
          <Route path="question-papers/*" element={<QpgenRoute><QpgenRoutes /></QpgenRoute>} />{/* [QPGEN-1 Q3b] a teacher's own papers (and books, while the school lets teachers upload); no route without the `qpgen` flag (QpgenRoute: home, no request). On the route's own line: a naive comment-stripper reads the path's slash-star as a comment start */}
          <Route path="school" element={<SchoolProfilePage />} />
          {/* [REQ-AUG-30 ACCESS-1 1.0a] The granted library desk. Registration point 4 of 4 for a
              teacher (nav · mobile More · bottom tabs · this route) — Q17 confirms no palette exists
              in this shell, so there is no fifth. */}
          <Route path="library" element={<CapabilityGate capability="CAP_LIBRARY" label="Library"><AdminLibrary /></CapabilityGate>} />
          {/* [REQ-AUG-30 ACCESS-1 1.0b] The granted collection desk. Registration point 4 of 4 for a
              teacher, same as Library above. */}
          <Route path="fees" element={<CapabilityGate capability="CAP_FEES" label="Fee Collection"><AdminFeeCollection /></CapabilityGate>} />
          {/* BF-1: valid match target for the mobile More tab; TeacherShell's
              isMorePage check renders MobileMorePage instead of this Outlet */}
          <Route path="more" element={<></>} />
          <Route path="student/:id" element={<TeacherStudentProfile />} />
        </Route>

        {/* ── Employee (NON_TEACHING / ADMINISTRATIVE) ── */}
        <Route
          path="/employee"
          element={
            <DevOrProtected roles={['EMPLOYEE']}>
              <InactivityGuard role={userRole}>
                <EmployeeShell />
              </InactivityGuard>
            </DevOrProtected>
          }
        >
          <Route index element={<EmployeeDashboard />} />
          <Route path="attendance" element={<EmployeeAttendance />} />
          <Route path="my-trips" element={<TransportTripRoster />} />{/* REQ-AUG-26 Band 3 */}
          <Route path="leave" element={<EmployeeLeave />} />
          <Route path="salary" element={<EmployeeSalary />} />
          <Route path="profile" element={<EmployeeProfile />} />
          <Route path="shift" element={<EmployeeMyShift />} />
          <Route path="notifications" element={<EmployeeNotifications />} />
          <Route path="my-tickets" element={<MyTickets />} />{/* [PORTAL-FIX-1 R3] */}
          <Route path="notification-preferences" element={<NotificationPreferences />} />
          <Route path="holidays" element={<EmployeeHolidayCalendar />} />
          <Route path="school" element={<SchoolProfilePage />} />
          {/* [REQ-AUG-30 ACCESS-1 1.0a] Same mounted component as the teacher and admin shells.
              A non-teaching librarian is exactly the person this capability was designed for. */}
          <Route path="library" element={<CapabilityGate capability="CAP_LIBRARY" label="Library"><AdminLibrary /></CapabilityGate>} />
          {/* [REQ-AUG-30 ACCESS-1 1.0b] Same mounted component. A non-teaching office clerk is exactly
              the person CAP_FEES was designed for. */}
          <Route path="fees" element={<CapabilityGate capability="CAP_FEES" label="Fee Collection"><AdminFeeCollection /></CapabilityGate>} />
          {/* EMP-MORE: valid match target for the mobile More tab; EmployeeShell's
              isMorePage check renders MobileMorePage instead of this Outlet */}
          <Route path="more" element={<></>} />
        </Route>

        {/* ── OLYMPIAD-P4-A exam (STUDENT, full-screen OUTSIDE StudentShell — no bottom nav/sidebar) ── */}
        <Route
          path="/olympiad/exam/:testId"
          element={
            <DevOrProtected roles={['STUDENT']}>
              <OlympiadExamPage />
            </DevOrProtected>
          }
        />

        {/* ── Student ── */}
        <Route
          path="/student/*"
          element={
            <DevOrProtected roles={['STUDENT']}>
              <InactivityGuard role={userRole}>
                <StudentShell />
              </InactivityGuard>
            </DevOrProtected>
          }
        />

        {/* ── App Owner ── */}
        <Route
          path="/owner/*"
          element={
            <DevOrProtected roles={['APP_OWNER']}>
              <InactivityGuard role="APP_OWNER">
                <OwnerShell />
              </InactivityGuard>
            </DevOrProtected>
          }
        />

        {/* ── Root redirect ── */}
        <Route
          path="/"
          element={
            user
              ? <Navigate to={getDefaultPath(userRole, employeeCategory)} replace />
              : <Navigate to="/login" replace />
          }
        />

        {/* ── 404 fallback ── */}
        <Route
          path="*"
          element={
            user
              ? <Navigate to={getDefaultPath(userRole, employeeCategory)} replace />
              : <Navigate to="/login" replace />
          }
        />
      </Routes>
    </Suspense>
    {/* IB-1: single app-level mount — shows on login AND inside portals; its own
        mobile/standalone/desktop gates decide per-screen visibility. */}
    <PWAInstallPrompt />
    </>
  );
}